/// <reference types="vitest" />
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  GoogleAuthProvider,
  sendSignInLinkToEmail,
  signInWithCredential,
  signInWithPopup,
  User,
  UserCredential,
  onIdTokenChanged,
} from "firebase/auth";

import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "integrations/firebase/auth";
import { GlobalLoading } from "components/loading";
import { logError } from "shared/services/ErrorReporting";
import { jwtDecode } from "jwt-decode";
import { t } from "i18next";
import { toast } from "@/utils/toast";

export interface IAuthContext {
  authUser?: User | null;
  loading: boolean;
  isLoggedIn: boolean;
  authToken?: string;

  signInWithOauth: (type: OAuthProviderType) => Promise<UserCredential | void>;

  signInAsEmulatorTestUser?: () => Promise<UserCredential | void>;

  sendLoginLink: (email: string) => Promise<void>;

  signOut: () => Promise<void>;
}

export const AuthContext = createContext<IAuthContext>({
  isLoggedIn: false,
  loading: true,
  sendLoginLink: async () => {},
  signInWithOauth: async () => {},
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

const OAUTH_PROVIDERS = {
  google: new GoogleAuthProvider(),
};

type OAuthProviderType = keyof typeof OAUTH_PROVIDERS;

const isAuthEmulator = import.meta.env.VITE_USE_FIREBASE_EMULATOR === "true";

// The Auth Emulator accepts unsigned JSON as a Google ID token; this matches the seeded "Anna Access (Domain)" account.
const EMULATOR_TEST_USER_ID_TOKEN = JSON.stringify({
  sub: "6265994281528084249986695100759439399801",
  email: "anna.access@chaptr.com",
  email_verified: true,
  name: "Anna Access (Domain)",
});

const reportLoginError = (error: unknown) => {
  logError(error);
  toast.error(t("login:error.loginFailed"));
};

export interface UserProfile {
  firstName: string;
  lastName: string;
  email: string;
  userId: string;
  photoUrl?: string;
}

export const AuthContextProvider = ({ children }: PropsWithChildren) => {
  const [idToken, setIdToken] = useState<{ uid: string; token: string }>();
  const [authUser, loading] = useAuthState(auth);
  const authToken =
    idToken && idToken.uid === authUser?.uid ? idToken.token : undefined;

  const signOut = useCallback(() => auth.signOut(), []);

  const signInWithOauth = useCallback(async (type: OAuthProviderType) => {
    const provider = OAUTH_PROVIDERS[type];

    if (!provider) {
      throw new Error(`No provider found for type ${type}`);
    }

    try {
      const result = await signInWithPopup(auth, provider);
      toast.success(t("login:success.toast.title"));
      return result;
    } catch (e) {
      reportLoginError(e);
    }
  }, []);

  const signInAsEmulatorTestUser = useCallback(async () => {
    try {
      const result = await signInWithCredential(
        auth,
        GoogleAuthProvider.credential(EMULATOR_TEST_USER_ID_TOKEN),
      );
      toast.success(t("login:success.toast.title"));
      return result;
    } catch (e) {
      reportLoginError(e);
    }
  }, []);

  const sendLoginLink = useCallback(async (email: string) => {
    try {
      window.localStorage.setItem("emailForSignIn", email);

      await sendSignInLinkToEmail(auth, email, {
        handleCodeInApp: true,
        url: `${window.location.origin}/login-link`,
      });

      toast.success(t("login:link.success.linkSent", { email }));
    } catch (e) {
      reportLoginError(e);
      window.localStorage.removeItem("emailForSignIn");
    }
  }, []);

  useEffect(() => {
    if (!authUser) return;

    const updateToken = async (user: User, forceUpdate: boolean = false) => {
      try {
        const token = await user.getIdToken(forceUpdate);
        setIdToken({ uid: user.uid, token });
        return token;
      } catch (err) {
        logError(err);
        setIdToken(undefined);
      }
    };

    let refreshTimeout: NodeJS.Timeout;
    const unsubscribe = onIdTokenChanged(auth, async (user) => {
      if (user) {
        const token = await updateToken(user, true);
        if (!token) return;

        const decodedToken = jwtDecode(token);
        if (!decodedToken.exp) {
          logError("Token decoding failed.");
          return;
        }
        const expirationTime = decodedToken.exp * 1000;
        const timeToRefresh = expirationTime - Date.now() - 5 * 60 * 1000;

        refreshTimeout = setTimeout(() => {
          updateToken(user, true);
        }, timeToRefresh);
      } else {
        setIdToken(undefined);
      }
    });

    return () => {
      unsubscribe();
      if (refreshTimeout) {
        clearTimeout(refreshTimeout);
      }
    };
  }, [authUser]);

  const isLoggedIn = !!authUser;
  const authProviderValue = useMemo(
    () => ({
      authUser,
      loading,
      isLoggedIn,
      signInWithOauth,
      signInAsEmulatorTestUser: isAuthEmulator
        ? signInAsEmulatorTestUser
        : undefined,
      signOut,
      sendLoginLink,
      authToken,
    }),
    [
      authUser,
      loading,
      isLoggedIn,
      signInWithOauth,
      signInAsEmulatorTestUser,
      signOut,
      sendLoginLink,
      authToken,
    ],
  );

  if (loading) {
    return <GlobalLoading />;
  }

  return <AuthContext value={authProviderValue}>{children}</AuthContext>;
};
